import { memo, useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import dynamic from "next/dynamic"
import Button from "@/components/core/Button/Button"
import Modal from "@/components/core/Modal/Modal"
import styles from "@/components/Shared/UpsellInstallServices/ProductCardForUpsell/index.module.scss"
import useWindowResize from "@/hooks/useWindowResize"
import { getPresetUrl } from "@/utils/index"
import { getPDPUrl } from "@/utils/helper"
import {
  addAnalyticsForAddToCart,
  addClickAnalytics,
} from "@/components/Shared/UpsellInstallServices/ProductCardForUpsell/analytics/index"
import {
  quickViewOpenAnalytics,
  quickViewCloseAnalytics,
} from "@/components/Shared/QuickView/analytics.js"
import Image from "@/components/Shared/Image"
import { DOMAIN } from "@/constants"
import { preventPopupScroll } from "@/components/Shared/QuickView/utlis"
import { getStock } from "@/utils/cart"
import { selectGenericState, showToast } from "@/store/features/genericSlice"

const QuickView = dynamic(() => import("@/components/Shared/QuickView"))

const ProductCard = ({
  persona = "GST",
  currencySign = "$",
  brand = "kohler",
  presetConfigs = "",
  rowCols = 0,
  swatchUrl: baseUrl = "",
  outOfStockTitle = "",
  addToCart = () => {},
  staticText = {},
  productData = {},
  quickViewAuthData = {},
  quickViewButtonLabel = "",
  ...item
}) => {
  const [width] = useWindowResize()
  const [pdpUrl, setPdpUrl] = useState("")
  const [loading, setLoading] = useState(false)
  const [showQuickViewModal, setShowQuickViewModal] = useState(false)
  const dispatch = useDispatch()

  const { globalConfig = {} } = useSelector(selectGenericState)
  const { apiEndpoints: { stockFailureErrorMsg = "" } = {} } = globalConfig

  const {
    sku_s: skuId = "",
    quantity: productQuantity = 0,
    ProductBrandName_s: name = "",
    productName_s: productName = "",
    [`priceList.${persona}.price_d`]: productPrice = 0,
    [`priceList.${persona}.discountedPrice_d`]: discountedPrice = 0,
    ["productImages.labelWithUrl_ss"]: labledImages = [],
    Product_Category: productCategory = "",
    slug_s: slug = "",
    ctProductType_s: productType = "",
  } = item

  useEffect(() => {
    getPDPUrl(productCategory, slug).then(url => setPdpUrl(url))
  }, [])

  const price = Number(productPrice).toLocaleString(undefined, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  })
  const productPresetConfigs = presetConfigs ? JSON.parse(presetConfigs) : {}
  let imgId = ""
  if (labledImages.length) {
    const index = labledImages
      .map(item => (item.split("|")?.length ? item.split("|")[0] : ""))
      .indexOf(skuId)
    const assetId = labledImages[index]?.split("|")?.length
      ? labledImages[index].split("|")[1]
      : ""
    imgId = assetId
  }

  const handleAddToCartCb = () => {
    setLoading(false)
  }

  const handleAddToCart = async e => {
    e.preventDefault()
    e.stopPropagation()
    setLoading(true)
    const { isError } = await getStock(skuId)

    if (isError) {
      dispatch(
        showToast({
          isVisible: true,
          message: stockFailureErrorMsg,
        })
      )
      setLoading(false)
      return
    }

    const payload = [
      {
        sku: skuId,
        quantity: 1,
        brand: brand?.toLowerCase(),
        isBundle: productType === "engineer-bundle",
      },
    ]
    addToCart(payload, handleAddToCartCb)
    addAnalyticsForAddToCart(
      item,
      productPrice,
      discountedPrice,
      productImageUrl
    )
  }

  const productImageUrl = getPresetUrl(
    width,
    rowCols,
    baseUrl,
    imgId,
    productPresetConfigs
  )

  const productUrl = `${DOMAIN}${pdpUrl}${skuId ? "?skuId=" + skuId : ""}` || ""
  const linkClicked = () => {
    addClickAnalytics({
      item,
      productPrice,
      discountedPrice,
      productImageUrl,
      productUrl,
    })
  }

  const openQuickViewModal = () => {
    quickViewOpenAnalytics({
      productData,
      productUrl,
      productImageUrl,
      upshellmodal: true,
    })
    setShowQuickViewModal(true)
  }
  const closeQuickViewModal = () => {
    setShowQuickViewModal(false)
    preventPopupScroll(true)
  }

  useEffect(() => {
    if (showQuickViewModal) {
      preventPopupScroll()
    }
  }, [showQuickViewModal])

  const [productInfo, setProductInfo] = useState({})
  const handleProductInfo = data => {
    setProductInfo(data)
  }

  const handleButtonClosed = () => {
    quickViewCloseAnalytics({
      productInfo,
      upshellmodal: true,
    })
  }

  return (
    <div className={styles.curatedProduct}>
      <div className="curated-product-recommendation__card-list">
        <a
          href={`${pdpUrl}${skuId ? "?skuId=" + skuId : ""}`}
          onClick={linkClicked}
        >
          <div className="curated-product-recommendation__product-card">
            <div className="curated-product-recommendation__img">
              <Image
                src={productImageUrl}
                alt={name}
                width={280}
                height={280}
              />
            </div>
            <div className="curated-product-recommendation__product-name">
              {name || productName}
            </div>
            <div className="curated-product-recommendation__product-sku">
              {skuId}
            </div>
            {discountedPrice > 0 ? (
              <div className="curated-product-recommendation__discounted-price">
                <div className="price-with-discount">{`${currencySign}${discountedPrice}`}</div>
                <div className="original-price">{`${currencySign}${price}`}</div>
              </div>
            ) : (
              <div className="curated-product-recommendation__product-price">
                {`${currencySign}${price}`}
              </div>
            )}
            {!productQuantity ? (
              <div className="curated-product-recommendation__out-of-stock">
                {outOfStockTitle}
              </div>
            ) : (
              <div className="cart-quickview-section">
                <div
                  className={`curated-product-recommendation__add-to-cart${
                    !quickViewButtonLabel ? " upshell-button" : ""
                  }`}
                >
                  <Button
                    role="button"
                    label={"Add To Cart"}
                    size="small"
                    type="primary"
                    flexible={true}
                    onClick={handleAddToCart}
                    loading={loading}
                  />
                </div>
                {quickViewButtonLabel ? (
                  <div className="quick-view-title">
                    <Button
                      role="button"
                      label={quickViewButtonLabel}
                      size="small"
                      type="ghost"
                      ariaLabel={quickViewButtonLabel}
                      flexible={true}
                      onClick={e => {
                        e.preventDefault()
                        e.stopPropagation()
                        openQuickViewModal()
                      }}
                    />
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </a>
      </div>
      <Modal
        showModal={showQuickViewModal}
        onModalClose={closeQuickViewModal}
        className="quickview-modal"
        isCloseButtonClicked={handleButtonClosed}
      >
        <QuickView
          productData={productData}
          authorData={quickViewAuthData}
          closeModal={closeQuickViewModal}
          upshellmodal={true}
          handleProductInfo={handleProductInfo}
        />
      </Modal>
    </div>
  )
}
export default memo(ProductCard)
